import React from "react";
import { ArrowUpRight, MapPin, Ruler, Truck, PenTool } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const badges = [
  { icon: MapPin, label: "Fabrication locale" },
  { icon: Ruler, label: "Sur mesure" },
  { icon: Truck, label: "Livraison & installation" },
  { icon: PenTool, label: "Conception SAD" },
];

const items = [
  {
    title: "Buffet",
    desc: "Bahut, enfilade, vaisselier — finitions bois et façades sur mesure.",
    img: "/mobilier-buffet.png",
  },
  {
    title: "Meuble TV",
    desc: "Banc TV mural ou intégré avec rangements pensés pour vos équipements.",
    img: "/mobilier-meubletv.jpg",
  },
  {
    title: "Dressing",
    desc: "Dressings ouverts, fermés, sous-pente — chaque cm² optimisé.",
    img: "/portfolio-4-dressing.jpg",
  },
  {
    title: "Bureau",
    desc: "Plan de travail, rangements intégrés, ergonomie du quotidien.",
    img: "https://images.unsplash.com/photo-1593062096033-9a26b09da705?auto=format&fit=crop&w=1200&q=80",
  },
  {
    title: "Table",
    desc: "Table à manger, console, table basse — bois massif ou plaqué.",
    img: "/mobilier-table.jpg",
  },
  {
    title: "Claustra",
    desc: "Cloison ajourée bois — séparer sans cloisonner, lumière préservée.",
    img: "/mobilier-claustra.jpg",
  },
  {
    title: "Mobilier professionnel",
    desc: "Comptoir, banque d'accueil, présentoir — adapté à votre activité.",
    img: "/service-commerce.png",
  },
];

export default function Furniture() {
  return (
    <section
      id="mobilier"
      data-testid="furniture-section"
      className="bg-white py-24 sm:py-32"
    >
      <div className="mx-auto max-w-7xl px-6">
        {/* Section header */}
        <div className="mb-12 grid grid-cols-1 gap-8 md:grid-cols-12 md:items-end">
          <div className="md:col-span-7">
            <p className="mb-4 text-xs uppercase tracking-[0.28em] text-orange-800">
              Notre atelier
            </p>
            <h2 className="text-3xl font-light leading-[1.05] tracking-tight text-stone-900 sm:text-5xl lg:text-6xl">
              Mobilier sur mesure
              <br />
              <span className="italic text-stone-500">by SAD</span>
            </h2>
          </div>
          <div className="md:col-span-5">
            <p className="text-base leading-relaxed text-stone-600">
              SAD ne se contente pas d'agencer vos espaces&nbsp;: nous{" "}
              <span className="text-stone-900">dessinons, faisons fabriquer
              localement et livrons</span> le mobilier qui les compose. Chaque
              pièce est pensée pour votre lieu.
            </p>
          </div>
        </div>

        {/* Badges */}
        <div className="mb-14 flex flex-wrap gap-2">
          {badges.map((b) => {
            const Icon = b.icon;
            return (
              <span
                key={b.label}
                data-testid={`furniture-badge-${b.label.toLowerCase().replace(/\s|&/g, "-")}`}
                className="inline-flex items-center gap-2 rounded-full border border-stone-200 bg-stone-50 px-4 py-2 text-xs font-medium uppercase tracking-[0.18em] text-stone-700"
              >
                <Icon className="h-3.5 w-3.5 text-orange-800" strokeWidth={1.5} />
                {b.label}
              </span>
            );
          })}
        </div>

        {/* Grid 4 cols → first row 4, second row 3 with last card being CTA */}
        <div className="grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-stone-200 bg-stone-200 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((it, i) => (
            <a
              href="#devis"
              onClick={scrollToId("devis")}
              key={it.title}
              data-testid={`furniture-${it.title.toLowerCase().replace(/\s|&/g, "-")}`}
              className="group relative flex flex-col bg-white p-5 transition-colors hover:bg-stone-50"
            >
              <div className="relative mb-5 aspect-[4/3] overflow-hidden rounded-xl bg-stone-100">
                <img
                  src={it.img}
                  alt={it.title}
                  className="absolute inset-0 h-full w-full object-cover img-zoom"
                />
              </div>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-lg font-medium tracking-tight text-stone-900">{it.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-stone-500">{it.desc}</p>
                </div>
                <span className="mt-1 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-stone-200 text-stone-400 transition group-hover:border-orange-800 group-hover:bg-orange-800 group-hover:text-stone-50">
                  <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.5} />
                </span>
              </div>
            </a>
          ))}

          {/* CTA tile to fill the grid (8th cell) */}
          <a
            href="#devis"
            onClick={scrollToId("devis")}
            data-testid="furniture-cta-card"
            className="group relative flex flex-col justify-between bg-stone-900 p-5 text-stone-50 transition-colors hover:bg-orange-900"
          >
            <div className="aspect-[4/3] rounded-xl bg-stone-50/5" />
            <div className="mt-5">
              <p className="text-xs uppercase tracking-[0.22em] text-orange-200">
                Votre mobilier
              </p>
              <h3 className="mt-2 text-xl font-medium leading-tight tracking-tight">
                Créer votre espace
                <br />
                sur mesure →
              </h3>
            </div>
          </a>
        </div>
      </div>
    </section>
  );
}
